import { ApiError, api } from './api.js';
import { downscale, postWithProgress } from './vehicles.js';
import { onlyDigits } from '../lib/masks.js';

const enc = encodeURIComponent;

/**
 * Vitrine de oficinas (/v1/showcase) e perfil público editado pelo responsável (/v1/workshops/mine/...).
 */
export const showcase = {
  /** @param {{ lat?: number, lng?: number, city?: string, state?: string, specialty?: string, openNow?: boolean, verified?: boolean, minRating?: number, sort?: 'distance' | 'rating', limit?: number }} q */
  search: (q = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false).map(([k, v]) => [k, String(v)]));
    return api('GET', `/v1/showcase/workshops?${qs}`);
  },
  get: (id) => api('GET', `/v1/showcase/workshops/${enc(id)}`),
  /** Conta um clique para o painel da oficina (falha silenciosa: nunca atrapalha o clique). */
  track: (id, type) => api('POST', `/v1/showcase/workshops/${enc(id)}/events`, { type }).catch(() => {}),
  specialties: () => api('GET', '/v1/showcase/specialties'),

  // responsável pela oficina
  profile: () => api('GET', '/v1/workshops/mine/profile'),
  updateProfile: (patch) => api('PATCH', '/v1/workshops/mine/profile', patch),
  setLocation: (p) => api('PUT', '/v1/workshops/mine/location', p),
  reorderPhotos: (ids) => api('PUT', '/v1/workshops/mine/photos/order', { ids }),
  removePhoto: (id) => api('DELETE', `/v1/workshops/mine/photos/${enc(id)}`),
  stats: (days = 30) => api('GET', `/v1/workshops/mine/stats?days=${days}`),
  // selo Empresa Verificada
  verification: () => api('GET', '/v1/workshops/mine/verification'),
  requestVisit: () => api('POST', '/v1/workshops/mine/verification/request'),
};

/**
 * Foto da galeria: reduz para JPEG (até 1600 px), envia direto para o S3 e confirma.
 * Devolve o perfil atualizado.
 */
export async function uploadWorkshopPhoto(file, onProgress = () => {}) {
  if (!file.type.startsWith('image/')) throw new ApiError(0, 'INVALID_FILE', 'Escolha uma foto.');
  let blob;
  try {
    blob = await downscale(file, 1600, 0.8);
  } catch {
    throw new ApiError(0, 'INVALID_FILE', 'Não foi possível ler esta foto. Tente outra.');
  }
  const { photo, upload } = await api('POST', '/v1/workshops/mine/photos', { contentType: 'image/jpeg' });
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
  form.append('file', blob, 'oficina.jpg'); // "file" precisa ser o último campo
  await postWithProgress(upload.url, form, onProgress);
  return (await api('POST', `/v1/workshops/mine/photos/${enc(photo.id)}`)).profile;
}

/** Endereço pelo CEP (ViaCEP, gratuito, sem chave). Devolve null se o CEP não existir. */
export async function lookupCep(cep) {
  const d = onlyDigits(cep);
  if (d.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
    const j = await r.json();
    if (j.erro) return null;
    return { street: j.logradouro ?? '', district: j.bairro ?? '', city: j.localidade ?? '', state: j.uf ?? '' };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------ formatação e links */

export const maskCep = (v) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');

/** 1.2 → "1,2 km"; 0.3 → "300 m" */
export function fmtDistance(km) {
  if (km === null || km === undefined) return '';
  if (km < 1) return `${Math.max(100, Math.round((km * 1000) / 100) * 100)} m`;
  return `${km.toLocaleString('pt-BR', { maximumFractionDigits: km < 10 ? 1 : 0 })} km`;
}

export const fmtRating = (avg) => avg.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** WhatsApp com DDD → link wa.me (acrescenta o 55 do Brasil). */
export function whatsappLink(number, text = '') {
  const d = onlyDigits(number);
  if (!d) return '';
  const full = d.length <= 11 ? `55${d}` : d;
  return `https://wa.me/${full}${text ? `?text=${enc(text)}` : ''}`;
}
export const phoneLink = (number) => (number ? `tel:${onlyDigits(number)}` : '');
/** Rota no Google Maps (app ou navegador), sem SDK de mapa. */
export const routeLink = (loc) => (loc ? `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}` : '');
export const mapLink = (loc) => (loc ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}` : '');

const DAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const hh = (t) => (t.endsWith(':00') ? `${Number(t.slice(0, 2))}h` : `${Number(t.slice(0, 2))}h${t.slice(3)}`);

/** Horário da semana em uma linha: "seg a sex 8h–12h, 13h–18h · sáb 8h–12h". */
export function hoursSummary(hours) {
  if (!hours?.length) return '';
  const key = (d) => hours.filter((h) => h.day === d).sort((a, b) => a.open.localeCompare(b.open)).map((h) => `${hh(h.open)}–${hh(h.close)}`).join(', ');
  const order = [1, 2, 3, 4, 5, 6, 0]; // semana começando na segunda
  const groups = [];
  for (const d of order) {
    const k = key(d);
    if (!k) continue;
    const last = groups.at(-1);
    if (last && last.k === k && order.indexOf(d) === order.indexOf(last.to) + 1) last.to = d;
    else groups.push({ k, from: d, to: d });
  }
  return groups.map((g) => `${g.from === g.to ? DAY_SHORT[g.from] : `${DAY_SHORT[g.from]} a ${DAY_SHORT[g.to]}`} ${g.k}`).join(' · ');
}

/** "há 2 anos" no TicketCar (a partir do cadastro). */
export function timeOnApp(since) {
  const months = Math.floor((Date.now() - new Date(since).getTime()) / (30.44 * 864e5));
  if (months < 1) return { value: 'Novo', label: 'no TicketCar' };
  if (months < 12) return { value: `${months} ${months === 1 ? 'mês' : 'meses'}`, label: 'no TicketCar' };
  const y = Math.floor(months / 12);
  return { value: `${y} ${y === 1 ? 'ano' : 'anos'}`, label: 'no TicketCar' };
}
